<template>
  <div v-if="visible" class="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-default p-4 sm:items-center sm:p-6">
    <div class="pointer-events-none fixed inset-0 overflow-hidden">
      <div class="absolute -top-24 -left-24 size-96 rounded-full bg-primary/10 blur-3xl" />
      <div class="absolute -right-24 -bottom-24 size-96 rounded-full bg-info/10 blur-3xl" />
    </div>

    <div class="relative flex w-full max-w-md flex-col items-center gap-5 rounded-2xl border border-default bg-default p-6 py-8 shadow-xl sm:p-8">
      <div class="flex flex-col items-center gap-2">
        <div class="flex size-16 items-center justify-center rounded-2xl bg-elevated shadow-sm ring-1 ring-default">
          <img :src="meltalIcon" alt="Meltal" class="size-11 object-contain" />
        </div>
        <div class="text-xl font-bold text-highlighted">{{ t('app.name') }}</div>
        <div class="flex items-center gap-1.5 text-sm text-muted">
          <UIcon name="i-lucide-lock" class="size-3.5" />
          {{ t('app.tagline') }} &middot; {{ t('lock.locked') }}
        </div>
      </div>

      <template v-if="step === 'who'">
        <div class="text-xs font-semibold tracking-wide text-muted uppercase">{{ t('lock.choose_who') }}</div>

        <div v-if="recentLogins.length > 0" class="flex w-full flex-col gap-2">
          <div class="text-xs text-muted">{{ t('lock.recent') }}</div>
          <div class="grid w-full grid-cols-2 gap-3">
            <button
              v-for="name in recentLogins"
              :key="name"
              type="button"
              class="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-default bg-elevated p-4 transition-all hover:border-primary hover:shadow-md active:scale-[0.98]"
              @click="startOperatorLogin(name)"
            >
              <span class="flex size-12 items-center justify-center rounded-full bg-primary text-base font-bold text-on-primary shadow-sm">
                {{ initials(name) }}
              </span>
              <span class="text-center text-sm leading-tight font-semibold text-highlighted">{{ name }}</span>
            </button>
          </div>
        </div>

        <USelectMenu
          v-model="pickedName"
          :items="operatorNames"
          :placeholder="recentLogins.length > 0 ? t('lock.pick_from_list') : t('lock.pick_from_list_all')"
          :search-input="{ placeholder: t('lock.search_placeholder') }"
          icon="i-lucide-search"
          size="xl"
          class="w-full"
          @update:model-value="onPickFromList"
        >
          <template #empty>{{ t('lock.no_results') }}</template>
        </USelectMenu>

        <div class="flex flex-col items-center gap-1">
          <UButton variant="link" color="neutral" @click="startAdminLogin">{{ t('lock.admin_login') }}</UButton>
          <UButton
            variant="link"
            color="neutral"
            size="sm"
            icon="i-lucide-book-open"
            to="/navodila/Navodila-Sortiranje.pdf"
            target="_blank"
          >
            {{ t('lock.guide_pdf') }}
          </UButton>
        </div>
      </template>

      <template v-else>
        <button type="button" class="flex items-center gap-1 self-start text-sm text-muted" @click="goBack">
          <UIcon name="i-lucide-chevron-left" class="size-4" />
          {{ t('general.back') }}
        </button>

        <div class="flex flex-col items-center gap-1 text-center">
          <div class="text-lg font-bold text-highlighted">{{ stepTitle }}</div>
          <div v-if="stepHint" class="max-w-xs text-sm text-muted">{{ stepHint }}</div>
        </div>

        <div class="flex gap-3" :class="{ 'animate-pulse': shake }">
          <span
            v-for="i in 4"
            :key="i"
            class="size-4 rounded-full border-2 transition-colors"
            :class="i <= pin.length ? (hasError ? 'border-error bg-error' : 'border-primary bg-primary') : 'border-default'"
          />
        </div>

        <div class="min-h-4 text-center text-sm text-error">{{ errorMessage }}</div>

        <div class="grid grid-cols-3 gap-3" role="group" :aria-label="t('lock.keypad')">
          <UButton
            v-for="key in DIGITS"
            :key="key"
            size="xl"
            color="neutral"
            variant="outline"
            class="h-16 w-16 justify-center text-xl transition-transform active:scale-95"
            @click="pressKey(key)"
          >
            {{ key }}
          </UButton>
          <UButton size="xl" color="neutral" variant="outline" class="h-16 w-16 justify-center text-xs transition-transform active:scale-95" @click="pressKey('cancel')">
            {{ t('general.cancel') }}
          </UButton>
          <UButton size="xl" color="neutral" variant="outline" class="h-16 w-16 justify-center text-xl transition-transform active:scale-95" @click="pressKey('0')">
            0
          </UButton>
          <UButton
            size="xl"
            color="neutral"
            variant="outline"
            class="h-16 w-16 justify-center transition-transform active:scale-95"
            :aria-label="t('lock.delete_digit')"
            @click="pressKey('del')"
          >
            <UIcon name="i-lucide-delete" class="size-5" />
          </UButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useEventListener } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import meltalIcon from '@/assets/meltal-icon.png'
import { isValidNewPin } from '@/libs/auth'
import { PrintSuccess } from '@/libs/toast'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { LoginResponse, TokenResponse } from '@/types/Common'
import type { IOperator } from '@/types/Operator'

type Step = 'who' | 'pin'
type Mode = 'login' | 'admin' | 'force-new' | 'force-confirm' | 'change-current' | 'change-new' | 'change-confirm'

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
const PIN_LENGTH = 4

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()
const fetchStore = useFetchStore()

const step = ref<Step>('who')
const mode = ref<Mode>('login')
const recentLogins = ref<string[]>([])
const operators = ref<IOperator[]>([])
const pickedName = ref<string>()
const targetOperator = ref('')
const pin = ref('')
const hasError = ref(false)
const shake = ref(false)
const errorMessage = ref('')
const busy = ref(false)

// Codes are only held in memory for the duration of a flow and wiped afterwards.
let currentPinStaged = ''
let newPinDraft = ''

// Shown while locked, while a forced first-code change is pending, or when another part of
// the app (session card, Šifranti) asked to reopen it for a change-pin / admin flow.
const visible = computed(() => !appStore.isLoggedIn() || appStore.mustChangePin || !!appStore.overlayRequest)

const operatorNames = computed(() =>
  operators.value.map((operator) => operator.name).sort((a, b) => a.localeCompare(b, 'sl')),
)

const stepTitle = computed(() => {
  switch (mode.value) {
    case 'admin':
      return t('lock.administrator')
    case 'force-new':
      return t('lock.force_title')
    case 'force-confirm':
      return t('lock.force_confirm')
    case 'change-current':
      return t('lock.current_pin')
    case 'change-new':
      return t('lock.new_pin')
    case 'change-confirm':
      return t('lock.confirm_pin')
    default:
      return targetOperator.value
  }
})

const stepHint = computed(() => (mode.value === 'force-new' ? t('lock.force_hint') : ''))

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((part, i, arr) => (i === 0 || i === arr.length - 1 ? part[0] : ''))
    .join('')
    .toUpperCase()

const loadLoginChoices = async () => {
  const [recentRes, operatorsRes] = await Promise.all([
    fetchStore.fetch('/recent-logins').get().json<string[] | null>(),
    fetchStore.fetch('/operators').get().json<IOperator[] | null>(),
  ])

  recentLogins.value = (recentRes.data.value ?? []).slice(0, 4)
  operators.value = operatorsRes.data.value ?? []
}

const clearSecrets = () => {
  currentPinStaged = ''
  newPinDraft = ''
  pin.value = ''
}

const showWho = () => {
  step.value = 'who'
  mode.value = 'login'
  pickedName.value = undefined
  errorMessage.value = ''
  clearSecrets()
}

const showPin = (nextMode: Mode) => {
  step.value = 'pin'
  mode.value = nextMode
  pin.value = ''
  errorMessage.value = ''
}

onMounted(loadLoginChoices)

// Back at the lock screen (manual lock, idle lock, 401): start from the person picker again.
watch(
  () => appStore.isLoggedIn(),
  (loggedIn) => {
    if (loggedIn) return
    showWho()
    loadLoginChoices()
  },
)

// Another part of the app reopened the overlay while logged in.
watch(
  () => appStore.overlayRequest,
  (request) => {
    if (request === 'admin') showPin('admin')
    else if (request === 'change-pin') showPin('change-current')
  },
  { immediate: true },
)

const startOperatorLogin = (name: string) => {
  targetOperator.value = name
  showPin('login')
}

const onPickFromList = (name: string | undefined) => {
  if (name) startOperatorLogin(name)
}

const startAdminLogin = () => showPin('admin')

const goBack = () => {
  if (mode.value === 'force-new' || mode.value === 'force-confirm') {
    // The forced code change cannot be skipped: going back signs the person out again.
    appStore.setLogout()
    return
  }

  if (appStore.overlayRequest) {
    clearSecrets()
    appStore.clearOverlayRequest()
    return
  }

  showWho()
}

const failPin = (message: string) => {
  hasError.value = true
  shake.value = true
  errorMessage.value = message

  setTimeout(() => {
    shake.value = false
    pin.value = ''
    hasError.value = false
  }, 320)
}

const pressKey = (key: string) => {
  if (busy.value) return

  if (key === 'cancel') {
    pin.value = ''
    return
  }

  if (key === 'del') {
    pin.value = pin.value.slice(0, -1)
    return
  }

  if (pin.value.length >= PIN_LENGTH) return

  errorMessage.value = ''
  pin.value += key

  if (pin.value.length === PIN_LENGTH) {
    setTimeout(submit, 120)
  }
}

// Physical keyboard works too (desktop): digits, Backspace, Escape.
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (!visible.value || step.value !== 'pin') return

  if (/^\d$/.test(event.key)) pressKey(event.key)
  else if (event.key === 'Backspace') pressKey('del')
  else if (event.key === 'Escape') pressKey('cancel')
})

const finishLogin = (stayOnPage = false) => {
  clearSecrets()
  appStore.clearOverlayRequest()
  if (!stayOnPage) router.push('/')
}

const submitLogin = async (path: '/login-operator' | '/login-admin', body: Record<string, string>) => {
  const { data, response } = await fetchStore.fetch(path).post(body).json<LoginResponse>()

  if (!response.value) {
    failPin(t('errors.network'))
    return
  }

  if (!data.value?.token) {
    failPin(t('lock.wrong_pin'))
    return
  }

  const usedPin = pin.value
  const wasElevation = appStore.isLoggedIn() && appStore.overlayRequest === 'admin'
  appStore.setLogin(data.value.token, data.value.must_change_pin)

  if (data.value.must_change_pin) {
    // The start code was just typed: keep it only to authorise the forced /change-pin call.
    currentPinStaged = usedPin
    showPin('force-new')
    return
  }

  PrintSuccess(path === '/login-admin' ? t('success.logged_in_admin') : t('success.logged_in_operator', { name: targetOperator.value }))
  finishLogin(wasElevation)
}

const submitNewPin = async (currentPin: string, newPin: string, forced: boolean) => {
  const { data, response, error } = await fetchStore
    .fetch('/change-pin')
    .post({ current_pin: currentPin, new_pin: newPin })
    .json<TokenResponse>()

  if (!response.value || error.value) {
    // Back to the first code step with the reason shown there.
    const message = !response.value ? t('errors.network') : forced ? t('lock.pin_not_allowed') : t('lock.wrong_pin')
    showPin(forced ? 'force-new' : 'change-current')
    errorMessage.value = message
    return
  }

  if (data.value?.token) appStore.setLogin(data.value.token)
  else appStore.mustChangePin = false

  PrintSuccess(forced ? t('success.code_chosen') : t('success.pin_changed'))
  finishLogin(!forced)
}

const submit = async () => {
  if (busy.value) return
  busy.value = true

  try {
    switch (mode.value) {
      case 'login':
        await submitLogin('/login-operator', { name: targetOperator.value, pin: pin.value })
        break

      case 'admin':
        await submitLogin('/login-admin', { pin: pin.value })
        break

      case 'force-new':
        if (!isValidNewPin(pin.value, currentPinStaged)) {
          failPin(t('lock.pin_not_allowed'))
          break
        }
        newPinDraft = pin.value
        showPin('force-confirm')
        break

      case 'force-confirm':
        if (pin.value !== newPinDraft) {
          newPinDraft = ''
          showPin('force-new')
          errorMessage.value = t('lock.pins_dont_match')
          break
        }
        await submitNewPin(currentPinStaged, newPinDraft, true)
        break

      case 'change-current':
        // Verified together with the new code in one request once it is confirmed.
        currentPinStaged = pin.value
        showPin('change-new')
        break

      case 'change-new':
        if (!isValidNewPin(pin.value, currentPinStaged)) {
          failPin(t('lock.pin_not_allowed'))
          break
        }
        newPinDraft = pin.value
        showPin('change-confirm')
        break

      case 'change-confirm':
        if (pin.value !== newPinDraft) {
          newPinDraft = ''
          showPin('change-new')
          errorMessage.value = t('lock.pins_dont_match')
          break
        }
        await submitNewPin(currentPinStaged, newPinDraft, false)
        break
    }
  } finally {
    busy.value = false
  }
}
</script>
